import { Component, DestroyRef, ElementRef, inject, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { SearchInputComponent } from '../../../../../components/inputs/search-input/search-input.component';
import {
  SortDropdownComponent,
  SortDropdownGroup,
  buildSortGroups,
} from '../../../../../components/inputs/sort-dropdown/sort-dropdown.component';
import { SegmentedTableComponent } from '../../../../../components/tables/segmented-table/segmented-table.component';

// services
import { ContactsService } from '../../../../../services/contacts/contacts.service';
import { GroupAudienceService } from '../../../../../services/segments/group-audience.service';
import { IOrganizationSort } from '../../organization-list/organization-list.component';
import { IEntitySegmentQuery } from '../../../../../components/organization/segments/segment-utilities';
import { MessageService } from '../../../../../services/message/message.service';
import { PromptCardComponent } from '../../../../../components/toasts-and-cards/prompt-card/prompt-card.component';
import { HasPermissionDirective } from '../../../../../directives/has-permission.directive';
import { countNoun } from '../../../../../components/tables/group-table/group-rows';

@Component({
  selector: 'list-segment',
  standalone: true,
  imports: [
    SearchInputComponent,
    SortDropdownComponent,
    SegmentedTableComponent,
    PromptCardComponent,
    HasPermissionDirective,
  ],
  templateUrl: './list-segment.html',
  styleUrls: ['../../../../../components/tables/group-table/group-list.css', './list-segment.css'],
})
export class ListSegmentedComponent {
  contactsService = inject(ContactsService);
  messageService = inject(MessageService);
  router = inject(Router);
  private audience = inject(GroupAudienceService);
  private destroyRef = inject(DestroyRef);
  private host: ElementRef<HTMLElement> = inject(ElementRef);

  @ViewChild(SegmentedTableComponent) tableChild?: SegmentedTableComponent;
  @ViewChild(SearchInputComponent) searchInput?: SearchInputComponent;

  handleAction(action: string) {
    if (this.selectedSegments.length === 0) return;
    switch (action) {
      case 'send-sms':
        this.sendSMS();
        break;
      case 'delete':
        this.openDeleteSegmentPrompt();
        break;
    }
  }

  sendSMS() {
    if (this.selectedSegments.length) {
      this.messageService.updateSelectedSegments(this.selectedSegments);
      this.router.navigate(['/organization/messages/create']);
    }
  }

  // reactive data
  selectedSegments: IEntitySegmentQuery[] = [];
  total: number | null = null;

  updateSelections(segments: IEntitySegmentQuery[]) {
    this.selectedSegments = [...segments];
  }

  /** Search and sort for the table. A new object on each change; page and size live in the table. */
  params: Record<string, any> = { sort: 'name,asc' };

  // delete selected segments
  showDeleteSegmentsPrompt: boolean = false;
  deleting = false;
  /** Contacts matching the ticked segments, for the delete prompt. */
  selectedMatches: { total: number; complete: boolean } | null = null;

  closeDeleteSegmentPrompt() {
    if (this.deleting) return;
    this.showDeleteSegmentsPrompt = false;
  }

  openDeleteSegmentPrompt() {
    this.showDeleteSegmentsPrompt = true;
    this.selectedMatches = null;
    const counts = this.selectedSegments
      .filter((segment) => !!segment.uuid)
      .map((segment) =>
        this.audience.memberCount({ kind: 'segment', uuid: segment.uuid! }).pipe(
          take(1),
          catchError(() => of(null))
        )
      );
    if (!counts.length) {
      this.selectedMatches = { total: 0, complete: false };
      return;
    }
    forkJoin(counts)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((values) => {
        let total = 0;
        let complete = true;
        values.forEach((value) => (typeof value === 'number' ? (total += value) : (complete = false)));
        this.selectedMatches = { total, complete };
      });
  }

  get selectionLabel(): string {
    return `${countNoun(this.selectedSegments.length, 'segment', 'segments')} selected`;
  }

  get deleteTitle(): string {
    const count = this.selectedSegments.length;
    return count === 1 ? `Delete ${this.selectedSegments[0]?.name || 'this segment'}?` : `Delete ${count} segments?`;
  }

  get deleteButton(): string {
    const count = this.selectedSegments.length;
    return count === 1 ? 'Delete segment' : `Delete ${count} segments`;
  }

  get deleteImpact(): string {
    const matches = this.selectedMatches;
    if (!matches) return 'Counting the contacts they match…';
    const single = this.selectedSegments.length === 1;
    if (!matches.complete || matches.total === 0) {
      return 'Only the saved conditions are removed. Contacts stay in your contact list.';
    }
    const reach = single
      ? `${countNoun(matches.total, 'contact matches', 'contacts match')} it today.`
      : `Together they match up to ${countNoun(matches.total, 'contact', 'contacts')} today.`;
    return `${reach} Only the saved conditions are removed. Contacts stay in your contact list.`;
  }

  deleteSelectedSegments() {
    const count = this.selectedSegments.length;
    if (!count || this.deleting) return;
    this.deleting = true;
    this.contactsService
      .deleteSelectedSegments(this.selectedSegments.map((segment) => segment.uuid as string))
      .subscribe({
        next: () => {
          this.deleting = false;
          this.selectedSegments.forEach((segment) =>
            this.audience.invalidate({ kind: 'segment', uuid: segment.uuid as string })
          );
          this.resetSelectedSegments();
          this.showDeleteSegmentsPrompt = false;
        },
        error: () => {
          this.deleting = false;
          this.showDeleteSegmentsPrompt = false;
          this.contactsService.showError(
            count === 1 ? 'The segment was not deleted. Try again.' : `The ${count} segments were not deleted. Try again.`
          );
        },
      });
  }

  searchTerm: string = '';
  sortCriteria: IOrganizationSort = 'name,asc';

  readonly sortGroups: SortDropdownGroup[] = buildSortGroups({
    nameField: 'name',
    createdField: 'createdAt',
    modifiedField: 'modifiedAt',
  });

  get countLine(): string {
    if (this.total === null) return '';
    const line = countNoun(this.total, 'segment', 'segments');
    return this.searchTerm.trim() ? `${line} ${this.total === 1 ? 'matches' : 'match'}` : line;
  }

  updateSortCriteria(criteria: IOrganizationSort) {
    this.sortCriteria = criteria;
    this.refresh();
  }

  updateSearchTerm(term: string) {
    this.searchTerm = term ?? '';
    this.refresh();
  }

  clearSearch() {
    this.searchInput?.writeValue('');
    this.searchTerm = '';
    this.refresh();
  }

  private refresh() {
    const params: Record<string, any> = { sort: this.sortCriteria };
    const term = this.searchTerm.trim();
    if (term) params['name.contains'] = term;
    this.params = params;
  }

  updateTotal(total: number) {
    this.total = total;
  }

  resetSelectedSegments() {
    this.selectedSegments = [];
    this.tableChild?.deSelectAllSegments();
    this.keepFocusOnPage();
  }

  /**
   * The selection bar goes when nothing is ticked. If it held focus, move
   * focus to the search box rather than letting it drop to the page top.
   */
  private keepFocusOnPage() {
    setTimeout(() => {
      const active = this.host.nativeElement.ownerDocument?.activeElement;
      if (active && active !== active.ownerDocument.body) return;
      this.host.nativeElement.querySelector<HTMLInputElement>('.gl-search input')?.focus();
    });
  }

  get reactiveParams() {
    return this.params;
  }
}
